/**
 * ImageCropUploader Component
 * 
 * Componente para subir imágenes OG con recorte obligatorio a 1200x630.
 * - Drag & drop o selección de archivo
 * - Preview con herramienta de recorte
 * - Compresión automática a JPG
 * - Subida a Vercel Blob Storage
 */

import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import imageCompression from 'browser-image-compression';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropUploaderProps {
  onUploadComplete: (url: string) => void;
  onCancel: () => void;
  statisticId?: string;
}

// Aspect ratio for OG images: 1200/630 ≈ 1.9047619
const ASPECT_RATIO = 1200 / 630;
const MIN_WIDTH = 600; // Minimum width for quality
const OUTPUT_WIDTH = 1200;
const OUTPUT_HEIGHT = 630;
const MAX_FILE_SIZE_MB = 10;
const OUTPUT_MAX_SIZE_KB = 200; // Target ~200KB for the final image

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

const ImageCropUploader: React.FC<ImageCropUploaderProps> = ({
  onUploadComplete,
  onCancel,
  statisticId
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    setError(null);
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor, selecciona un archivo de imagen válido');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`El archivo es demasiado grande. Máximo ${MAX_FILE_SIZE_MB}MB`);
      return;
    }
    
    setSelectedFile(file);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  // Handle drag & drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Handle file input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // When image loads, set initial crop
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    // Use naturalWidth/naturalHeight for actual image dimensions (not rendered size)
    const { naturalWidth, naturalHeight, width, height } = e.currentTarget;
    
    // Check minimum dimensions using natural (original) dimensions
    if (naturalWidth < MIN_WIDTH) {
      setError(`La imagen es demasiado pequeña. Ancho mínimo: ${MIN_WIDTH}px (tu imagen: ${naturalWidth}px)`);
      return;
    }
    
    // Use rendered dimensions for crop UI (width/height), but validation uses natural dimensions
    setCrop(centerAspectCrop(width, height, ASPECT_RATIO));
  }, []);

  // Crop and compress the image
  const cropAndCompress = useCallback(async (): Promise<Blob> => {
    if (!imgRef.current || !completedCrop) {
      throw new Error('No hay imagen o recorte disponible');
    }
    
    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('No se pudo crear el contexto del canvas');
    }
    
    // Set output dimensions
    canvas.width = OUTPUT_WIDTH;
    canvas.height = OUTPUT_HEIGHT;
    
    // Calculate scale
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Draw cropped image
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      OUTPUT_WIDTH,
      OUTPUT_HEIGHT,
    );
    
    // Convert to blob
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            reject(new Error('Error al crear el blob'));
            return;
          }
          
          // Compress if needed
          try {
            const file = new File([blob], 'og-image.jpg', { type: 'image/jpeg' });
            
            const compressedFile = await imageCompression(file, {
              maxSizeMB: OUTPUT_MAX_SIZE_KB / 1024,
              maxWidthOrHeight: OUTPUT_WIDTH,
              useWebWorker: false, // Disabled to avoid CSP issues with blob workers
              fileType: 'image/jpeg',
              initialQuality: 0.85,
            });
            
            resolve(compressedFile);
          } catch (error) {
            // If compression fails, use original blob
            resolve(blob);
          }
        },
        'image/jpeg',
        0.9
      );
    });
  }, [completedCrop]);

  // Upload to Vercel Blob
  const handleUpload = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      setError('Por favor, selecciona un área de recorte');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress('Procesando imagen...');
    setError(null);
    
    try {
      // Crop and compress
      setUploadProgress('Recortando y comprimiendo...');
      const compressedBlob = await cropAndCompress();
      
      // Convert to base64
      setUploadProgress('Preparando subida...');
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data URL prefix
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(compressedBlob);
      });
      
      // Upload to server
      setUploadProgress('Subiendo a Vercel Blob...');
      const filename = `og-${statisticId || 'image'}-${Date.now()}.jpg`;
      
      const response = await fetch('/api/images/upload-og', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          filename,
          data: base64,
          statisticId,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al subir la imagen');
      }
      
      const result = await response.json();
      
      setUploadProgress('¡Subida completada!');
      
      // Call callback with the URL
      onUploadComplete(result.url);
      
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido al subir la imagen');
    } finally {
      setIsUploading(false);
    }
  }, [completedCrop, cropAndCompress, statisticId, onUploadComplete]);

  // Reset to initial state
  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setError(null);
    setUploadProgress('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  return (
    <div className="space-y-4">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      
      {/* Upload zone or Preview */}
      {!previewUrl ? (
        // Drag & Drop Zone
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragging 
              ? 'border-primary bg-primary/5' 
              : 'border-gray-300 hover:border-primary hover:bg-gray-50'
            }
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            className="hidden"
          />
          
          <div className="space-y-3">
            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            
            <div>
              <p className="text-lg font-medium text-foreground">
                {isDragging ? 'Suelta la imagen aquí' : 'Arrastra una imagen o haz clic para seleccionar'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Formatos: JPG, PNG, WebP • Máximo {MAX_FILE_SIZE_MB}MB
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                La imagen se recortará a 1200×630 píxeles (formato OG)
              </p>
            </div>
          </div>
        </div>
      ) : (
        // Crop Preview
        <div className="space-y-4">
          <div className="bg-gray-100 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-2 text-center">
              Ajusta el área de recorte. La imagen final será de 1200×630 píxeles.
            </p>
            
            <div className="flex justify-center">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={ASPECT_RATIO}
                minWidth={100}
                className="max-h-96"
              >
                <img
                  ref={imgRef}
                  src={previewUrl}
                  alt="Preview"
                  onLoad={onImageLoad}
                  className="max-h-96"
                  style={{ maxWidth: '100%' }}
                />
              </ReactCrop>
            </div>
          </div>
          
          {/* Progress */}
          {isUploading && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{uploadProgress}</span>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-between gap-3">
            <button
              type="button"
              onClick={handleReset}
              disabled={isUploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cambiar imagen
            </button>
            
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={isUploading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              
              <button
                type="button"
                onClick={handleUpload}
                disabled={isUploading || !completedCrop}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Subiendo...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    OK - Subir imagen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCropUploader;

